import { sb } from '../../../workspace-project/functions/index.js'
import { getSDK } from '../../functions/sdk.js'
const TOTAL = 10000000
const ROUNDS = { 1250000: 'Founding', 2500000: 'Pre-seed', 12500000: 'Seed' }

const _valOf = (s) => s?.root?.companySettings?.current_valuation || 12000000
const _pct = (n) => n > 0 ? ((n / TOTAL) * 100).toFixed(2) + '%' : '0%'
const _valAt = (shares, s) => shares > 0 ? '$' + Math.round((shares / TOTAL) * _valOf(s)).toLocaleString() : '—'
const _fmt = (n) => n != null && n > 0 ? Number(n).toLocaleString() : '—'

const _fetchCapData = async (s) => {
  // Group grants by user. Users with a single grant collapse into one row
  // (parent = grant). Users with multiple grants get a summary parent row
  // + hidden sub-rows (one per grant) that expand on click.
  const people = s.root?.people || []
  const grants = s.root?.userGrants || []
  const rows = []
  const grantsByUser = new Map()
  for (const g of grants) {
    if (!grantsByUser.has(g.user_id)) grantsByUser.set(g.user_id, [])
    grantsByUser.get(g.user_id).push(g)
  }
  for (const p of people) {
    const userGrants = grantsByUser.get(p.userId) || []
    if (userGrants.length <= 1) {
      const g = userGrants[0] || {}
      rows.push({
        ...p,
        ...g,
        class: g.share_class || p.share_class || 'B',
        _profileEmail: p.email,
        _grantId: g.id
      })
      continue
    }
    // Multi-grant user: emit a parent summary + sub-rows.
    const totals = userGrants.reduce((a, g) => ({
      shares: a.shares + (g.shares || 0),
      vested: a.vested + (g.vested_shares || 0),
      salary: a.salary + (Number(g.salary_monthly) || 0),
      investment: a.investment + (Number(g.investment) || 0)
    }), { shares: 0, vested: 0, salary: 0, investment: 0 })
    const earliestStart = userGrants
      .map(g => g.vesting_start).filter(Boolean).sort()[0] || null
    const latestEnd = userGrants
      .map(g => g.vesting_end).filter(Boolean).sort().slice(-1)[0] || null
    // Parent status: 'left' only if every grant is 'left'; otherwise
    // 'active' (effective status — active vs completed — derives from the
    // aggregated end date in _effectiveStatus).
    const allLeft = userGrants.every(g => g.status === 'left')
    // Total span in months (earliest start → latest end) for the Vesting column
    const totalMonths = earliestStart && latestEnd
      ? Math.round((new Date(latestEnd) - new Date(earliestStart)) / (1000 * 60 * 60 * 24 * 30.44))
      : null
    rows.push({
      ...p,
      class: userGrants[0].share_class || p.share_class || 'B',
      _profileEmail: p.email,
      _isParent: true,
      _grantCount: userGrants.length,
      shares: totals.shares,
      vested_shares: totals.vested,
      salary_monthly: totals.salary || null,
      investment: totals.investment || null,
      vesting_start: earliestStart,
      vesting_end: latestEnd,
      vesting_months: totalMonths,
      status: allLeft ? 'left' : 'active'
    })
    for (const g of userGrants) {
      rows.push({
        ...p,
        ...g,
        class: g.share_class || p.share_class || 'B',
        _profileEmail: p.email,
        _grantId: g.id,
        _parentUserId: p.userId,
        _isSub: true
      })
    }
  }
  // Two-phase sort: parents/singles sorted by shares desc; sub-rows stay
  // immediately after their parent (not shuffled by share count).
  const parents = rows.filter(r => !r._isSub).sort((a, b) => (b.shares || 0) - (a.shares || 0))
  const subsByParent = new Map()
  for (const r of rows.filter(r => r._isSub)) {
    if (!subsByParent.has(r._parentUserId)) subsByParent.set(r._parentUserId, [])
    subsByParent.get(r._parentUserId).push(r)
  }
  const ordered = []
  for (const p of parents) {
    ordered.push(p)
    if (p._isParent && subsByParent.has(p.userId)) {
      ordered.push(...subsByParent.get(p.userId))
    }
  }
  s.root.update({ capTable: ordered, capLoaded: true, capEditMode: false })
}

const _saveRow = async (row) => {
  if (!row.user_id) return
  await getSDK().execute('workspaceProject.userProfiles', 'update', {
    id: row.user_id,
    payload: {
      shares: row.shares,
      salary_monthly: row.salary_monthly,
      vesting_start: row.vesting_start,
      vesting_end: row.vesting_end,
      vesting_months: row.vesting_months,
      status: row.status
    }
  })
}

const _vestBar = (shares, vested) => {
  if (!shares || shares <= 0) return '—'
  const pct = Math.min(100, Math.round(((vested || 0) / shares) * 100))
  return `${_fmt(vested)} (${pct}%)`
}

// ── Shared row style — all rows use this base ──
const _rowBase = {
  flow: 'x',
  align: 'center',
  gap: 'Z',
  childProps: { flex: '1', padding: 'Z V', fontSize: 'Z2', minWidth: '0' }
}

// Vested cell shared by _capRow and AdvisorSection — two-line readout:
// exact share count (green) + % of granted (caption). Returns '—' for rows
// that don't vest (investments; passed through as `isInv` check upstream).
const _vestedCell = {
  textAlign: 'right',
  flow: 'y',
  gap: '0',
  align: 'center right',
  VestedShares: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'green',
    text: (el, s) => _fmt(s.vested_shares || 0)
  },
  VestedPct: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'caption',
    text: (el, s) => {
      if (!s.shares) return ''
      const pct = Math.min(100, Math.round(((s.vested_shares || 0) / s.shares) * 100))
      return `${pct}%`
    }
  }
}

// ── Header row builder ──
const _headerRow = (cols) => {
  const obj = {
    ..._rowBase,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    childProps: { ..._rowBase.childProps, fontWeight: '700', letterSpacing: '.04em', textTransform: 'uppercase', color: 'caption', fontSize: 'Y1' }
  }
  cols.forEach((c, i) => {
    obj[`H${String(i).padStart(2, '0')}`] = {
      ...(c === 'Name' ? { flex: '2' } : {}),
      textAlign: ['Shares', 'Equity', 'Value', 'Invested', 'Monthly', 'Vested', 'Origin valuation'].includes(c) ? 'right' : 'left',
      text: c
    }
  })
  return obj
}

// Status derivation (end date wins over DB status):
//   end date in the past      → 'completed' — contract ran its course
//   DB status='left' (no date or future end) → 'ended' — terminated early
//   otherwise                 → 'active'
const _effectiveStatus = (s) => {
  const end = s.vesting_end
  if (end) {
    const endTs = new Date(end).getTime()
    if (!Number.isNaN(endTs) && endTs < Date.now()) return 'completed'
  }
  if (s.status === 'left') return 'ended'
  return 'active'
}

const _statusLabel = (v) =>
  v === 'active' ? 'Active'
    : v === 'ended' ? 'Ended'
      : v === 'completed' ? 'Completed'
        : v === 'left' ? 'Ended'
          : (v || '—')

const _statusColor = (v) =>
  v === 'active' ? 'green'
    : v === 'completed' ? 'blue'
      : 'red'

const _StatusPill = {
  tag: 'span',
  alignSelf: 'center',
  justifySelf: 'flex-start',
  padding: 'V Z',
  round: 'V',
  fontSize: 'Y2',
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  width: 'fit-content',
  background: (el, s) => `${_statusColor(_effectiveStatus(s))}.15`,
  color: (el, s) => _statusColor(_effectiveStatus(s)),
  text: (el, s) => _statusLabel(_effectiveStatus(s))
}

// ── Data row builder ──
const _capRow = (classType) => {
  const row = {
    ..._rowBase,
    hide: (el, s) => s._isSub && !(s.root?._capExpanded || {})[s._parentUserId],
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    background: (el, s) => s._isSub ? 'overlay' : 'transparent',
    cursor: 'pointer',
    transition: 'background 100ms ease',
    ':hover': { background: 'white.04' },
    // Parent rows toggle expansion. Non-parent rows (singletons + sub-rows)
    // open the equity-detail modal showing the exact grant record clicked.
    // To view the full profile, use the "Open profile" button in the modal.
    onClick: (e, el, s) => {
      if (s.root?.capEditMode) return
      if (s._isParent) {
        const cur = s.root?._capExpanded || {}
        s.root.update({ _capExpanded: { ...cur, [s.userId]: !cur[s.userId] } })
        return
      }
      // Find the full grant record (row state spreads ...p, ...g — so the
      // row already has the grant fields plus the person). Pull the fresh
      // record from root.userGrants so we're not showing a stale snapshot.
      const grant = (s.root?.userGrants || []).find(g => g.id === s._grantId)
      if (grant) s.root.update({ grantDetail: grant })
    },
    C01_Name: {
      flex: '2',
      flow: 'x',
      align: 'center left',
      gap: 'Z',
      minWidth: '0',
      paddingLeft: (el, s) => s._isSub ? 'A1' : 'Z',
      Caret: {
        hide: (el, s) => !s._isParent,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        flexShrink: '0',
        transition: 'transform 120ms ease',
        transform: (el, s) => (s.root?._capExpanded || {})[s.userId] ? 'rotate(90deg)' : 'rotate(0deg)',
        text: '▸'
      },
      NameText: {
        hide: (el, s) => !!s._isSub,
        tag: 'span',
        fontWeight: '600',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.name || '—'
      },
      GrantTag: {
        hide: (el, s) => !s.label || !s._isSub,
        tag: 'span',
        padding: 'V Z',
        round: 'V',
        fontSize: 'Y1',
        fontWeight: '700',
        color: 'caption',
        background: 'overlayStrong',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        flexShrink: '0',
        text: (el, s) => s.label || ''
      },
      GrantCount: {
        hide: (el, s) => !s._isParent,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        flexShrink: '0',
        text: (el, s) => `· ${s._grantCount} grants`
      }
    },
    C02_Shares: {
      textAlign: 'right',
      text: (el, s) => s.root?.capEditMode ? '' : _fmt(s.shares),
      Input: {
        tag: 'input', type: 'number', hide: (el, s) => !s.root?.capEditMode,
        padding: 'Y Z', round: 'Y', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
        background: 'overlayStrong', color: 'title', fontSize: 'Y2', width: '100%',
        value: (el, s) => s.shares || 0,
        onInput: (e, el, s) => { s.shares = parseInt(e.target.value) || 0 }
      }
    },
    C03_Equity: {
      textAlign: 'right',
      color: classType === 'A' ? 'blue' : classType === 'B' ? 'green' : classType === 'C' ? 'orange' : 'purple',
      text: (el, s) => _pct(s.shares)
    },
    C04_Value: { textAlign: 'right', color: 'caption', text: (el, s) => _valAt(s.shares, s) }
  }

  // Investment rows carry no vesting schedule — the money bought shares
  // outright. Show '—' across every vesting-related cell for those rows
  // so the table doesn't display bogus 0mo / missing date placeholders.
  const _isInv = (s) => s.investment != null && !s._isParent

  if (classType === 'A') {
    row.C05_Invested = { textAlign: 'right', color: 'caption', text: (el, s) => s.investment ? '$' + _fmt(s.investment) : '—' }
    row.C06_Vesting = { color: 'caption', text: (el, s) => _isInv(s) ? '—' : `${s.vesting_months || 0}mo` }
  } else if (classType === 'B') {
    row.C05_Monthly = { textAlign: 'right', color: 'caption', text: (el, s) => s.salary_monthly ? '$' + _fmt(Math.round(s.salary_monthly)) : '—' }
  }

  row.C07_Start = {
    color: 'caption',
    text: (el, s) => s.root?.capEditMode ? '' : (_isInv(s) ? '—' : (s.vesting_start?.slice(0, 10) || '—')),
    Input: {
      tag: 'input', type: 'date', hide: (el, s) => !s.root?.capEditMode || _isInv(s),
      padding: 'Y Z', round: 'Y', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
      background: 'overlayStrong', color: 'title', fontSize: 'Y2', width: '100%',
      value: (el, s) => s.vesting_start?.slice(0, 10) || '',
      onInput: (e, el, s) => { s.vesting_start = e.target.value || null }
    }
  }
  row.C08_End = {
    color: 'caption',
    text: (el, s) => s.root?.capEditMode ? '' : (_isInv(s) ? '—' : (s.vesting_end?.slice(0, 10) || '—')),
    Input: {
      tag: 'input', type: 'date', hide: (el, s) => !s.root?.capEditMode || _isInv(s),
      padding: 'Y Z', round: 'Y', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
      background: 'overlayStrong', color: 'title', fontSize: 'Y2', width: '100%',
      value: (el, s) => s.vesting_end?.slice(0, 10) || '',
      onInput: (e, el, s) => { s.vesting_end = e.target.value || null }
    }
  }
  row.C09_Vested = {
    ..._vestedCell,
    VestedShares: {
      ..._vestedCell.VestedShares,
      color: (el, s) => _isInv(s) ? 'caption' : 'green',
      text: (el, s) => _isInv(s) ? '—' : _fmt(s.vested_shares || 0)
    },
    VestedPct: {
      ..._vestedCell.VestedPct,
      text: (el, s) => {
        if (_isInv(s) || !s.shares) return ''
        const pct = Math.min(100, Math.round(((s.vested_shares || 0) / s.shares) * 100))
        return `${pct}%`
      }
    }
  }
  row.C10_Status = {
    flow: 'x',
    align: 'center left',
    Pill: {
      hide: (el, s) => !!s.root?.capEditMode,
      ..._StatusPill
    },
    Select: {
      tag: 'select', hide: (el, s) => !s.root?.capEditMode,
      padding: 'Y Z', round: 'Y', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
      background: 'overlayStrong', color: 'title', fontSize: 'Y2', cursor: 'pointer',
      onRender: (el, s) => {
        if (!el.node) return
        el.node.innerHTML = ''
        for (const v of ['active', 'left']) {
          const opt = document.createElement('option')
          opt.value = v; opt.textContent = _statusLabel(v)
          if (v === s.status) opt.selected = true
          el.node.appendChild(opt)
        }
      },
      onChange: (e, el, s) => { s.status = e.target.value }
    }
  }
  row.C11_Save = {
    hide: (el, s) => !s.root?.capEditMode,
    SaveBtn: {
      tag: 'button', text: 'Save', padding: 'Y Z1', round: 'Y', border: 'none',
      theme: 'primary', cursor: 'pointer', fontSize: 'Y1', fontWeight: '600',
      onClick: async (e, el, s) => { e.stopPropagation(); await _saveRow(s) }
    }
  }

  return row
}

// ── Section builder ──
// classFilter may be a string (matches r.class) or a predicate function.
const _section = (title, cols, classFilter, rowDef) => {
  const match = typeof classFilter === 'function'
    ? classFilter
    : (r) => r.class === classFilter
  return {
    flow: 'y',
    round: 'Z2',
    background: 'overlay',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    padding: 'A',

    Title: {
      tag: 'h2', fontSize: 'B', fontWeight: '800', color: 'title', margin: '0 0 Z 0',
      text: (el, s) => {
        const count = (s.root?.capTable || []).filter(r => match(r) && !r._isSub).length
        return classFilter === 'A' ? title : `${title} (${count})`
      }
    },

    Header: _headerRow(cols),

    Rows: {
      flow: 'y',
      childExtends: rowDef,
      childrenAs: 'state',
      children: (el, s) => (s.root?.capTable || []).filter(match)
    }
  }
}

const _colsA = ['Name', 'Shares', 'Equity', 'Value', 'Invested', 'Vesting', 'Start', 'End', 'Vested', 'Status']
const _colsB = ['Name', 'Shares', 'Equity', 'Value', 'Monthly', 'Start', 'End', 'Vested', 'Status']
// Investors: no Status (all active by definition) — show Origin valuation instead
const _colsInvestor = ['Name', 'Shares', 'Equity', 'Value', 'Origin valuation']
// Advisors vest like staff but without salary — show vesting schedule instead
const _colsAdvisor = ['Name', 'Shares', 'Equity', 'Value', 'Start', 'End', 'Vested', 'Status']

export const capTable = {
  extends: 'AppPage',
  metadata: { title: 'Cap table / Admin / Symbols Workspace' },
  onInit: (el, s) => {
    if (s.root?.currentUserId && !el.call('isSuperAdmin')) {
      let root = el; while (root && !root.routes) root = root.parent
      if (root) el.router('/admin', root, {}, { pushState: true })
    }
  },
  onCreate: (el, s) => {
    let retries = 0
    const tryLoad = () => {
      const hasPeople = (s.root?.people || []).length > 0
      const hasGrants = (s.root?.userGrants || []).length > 0
      if (hasPeople && hasGrants) return _fetchCapData(s)
      if (++retries > 25) {
        if (hasPeople) _fetchCapData(s)
        return
      }
      setTimeout(tryLoad, 200)
    }
    tryLoad()

    // Expose the refetch on root so other components (e.g. the modal
    // Save handler) can explicitly rebuild the cap table after they
    // mutate userGrants. DOMQL doesn't auto-call onUpdate on a page
    // element when root state changes, so we need an explicit hook.
    if (s.root) s.root.__refetchCapTable = () => _fetchCapData(s)
  },

  // Any time userGrants changes (e.g. after an edit from /profile) rebuild
  // the cap table so the staff rows reflect the latest grants without a
  // page reload. Gate on a version signature to avoid rebuild loops.
  onUpdate: (el, s) => {
    if (!s.root?.people?.length) return
    const grants = s.root?.userGrants || []
    const sig = grants.length + ':' + grants.map(g => `${g.id}:${g.salary_monthly}:${g.vesting_start}:${g.vesting_end}:${g.origin_valuation}:${g.shares_manual}:${g.share_class}:${g.status}`).join('|')
    if (el._lastGrantsSig === sig) return
    el._lastGrantsSig = sig
    _fetchCapData(s)
  },

  AdminSidebar: {},

  CapMain: {
    extends: 'AppMain',
    padding: '0',
    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Cap Table' } },
      RightGroup: {
        EditToggle: {
          tag: 'button', type: 'button', padding: 'Z A', round: 'Z',
          borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
          cursor: 'pointer', fontSize: 'Z2', fontWeight: '500',
          background: (el, s) => s.root?.capEditMode ? 'blue' : 'overlay',
          color: (el, s) => s.root?.capEditMode ? 'white' : 'title',
          text: (el, s) => s.root?.capEditMode ? 'Done' : 'Edit',
          onClick: async (e, el, s) => {
            if (s.root?.capEditMode) { await _fetchCapData(s) } else { s.root.update({ capEditMode: true }) }
          }
        }
      }
    },

    Body: {
      overflow: 'auto', padding: 'B', flow: 'y', gap: 'B',

      SummaryCard: {
        round: 'Z2', background: 'overlay', borderWidth: '1px', borderStyle: 'solid', borderColor: 'line', padding: 'B',
        SumTitle: { tag: 'h2', fontSize: 'B', fontWeight: '800', color: 'title', margin: '0 0 Z 0', text: 'Capital Structure' },
        SumSub: { tag: 'p', fontSize: 'Y2', color: 'caption', margin: '0 0 A 0', text: 'SYMBOLSAPP, INC — Delaware C-Corp · 10M shares · Rounds: $1.25M → $2.5M → $12.5M' },
        Stats: {
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'A',
          childrenAs: 'state',
          children: (el, s) => {
            // Count parent rows + singletons only (sub-rows are already aggregated into parents)
            const cap = (s.root?.capTable || []).filter(r => !r._isSub)
            const sumBy = (cls) => cap.filter(r => r.class === cls).reduce((a, r) => a + (r.shares || 0), 0)
            const alloc = cap.reduce((a, r) => a + (r.shares || 0), 0)
            const invested = cap.reduce((a, r) => a + (Number(r.investment) || 0), 0)
            const currentVal = s.root?.companySettings?.current_valuation || 0
            return [
              { label: 'Allocated', value: `${_fmt(alloc)} (${_pct(alloc)})` },
              { label: 'Class A', value: `${_fmt(sumBy('A'))} (${_pct(sumBy('A'))})` },
              { label: 'Class B', value: `${_fmt(sumBy('B'))} (${_pct(sumBy('B'))})` },
              { label: 'Class C', value: `${_fmt(sumBy('C'))} (${_pct(sumBy('C'))})` },
              { label: 'Invested', value: '$' + _fmt(invested) },
              { label: 'Current valuation', value: currentVal ? '$' + _fmt(currentVal) : '—' }
            ]
          },
          childExtends: {
            flow: 'y', gap: 'Y', padding: 'Z1', round: 'Z', background: 'overlayStrong',
            Val: { tag: 'span', fontSize: 'A', fontWeight: '800', color: 'title', text: (el, s) => s.value },
            Lab: { tag: 'span', fontSize: 'Y2', color: 'caption', fontWeight: '600', text: (el, s) => s.label }
          }
        }
      },

      ClassASection: _section('Founders · Class A', _colsA, 'A', _capRow('A')),
      ClassBSection: _section('Staff · Class B', _colsB, 'B', _capRow('B')),
      ClassCSection: _section('Staff · Class C', _colsB, 'C', _capRow('C')),

      // Investor section: any grant with investment set (across all classes).
      // Parents aggregate both grants + investments, so we skip them here and
      // show the individual investment sub-row / singleton instead.
      InvestorSection: _section(
        'Investors',
        _colsInvestor,
        (r) => r.investment != null && !r._isParent,
        {
          ..._rowBase,
          cursor: 'pointer',
          ':hover': { background: 'overlay' },
          onClick: (e, el, s) => {
            const grant = (s.root?.userGrants || []).find(g => g.id === s._grantId)
            if (grant) s.root.update({ grantDetail: grant })
          },
          C01_Name: {
            flex: '2',
            fontWeight: '600',
            color: 'title',
            flow: 'x',
            align: 'center left',
            gap: 'Z',
            NameText: { tag: 'span', text: (el, s) => s.name },
            ClassTag: {
              tag: 'span',
              padding: 'V Z',
              round: 'V',
              fontSize: 'Y1',
              fontWeight: '700',
              color: 'caption',
              background: 'overlayStrong',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'line',
              text: (el, s) => s.share_class || ''
            }
          },
          C02_Shares: { textAlign: 'right', text: (el, s) => _fmt(s.shares) },
          C03_Equity: { textAlign: 'right', color: 'purple', text: (el, s) => _pct(s.shares) },
          C04_Value: { textAlign: 'right', color: 'caption', text: (el, s) => s.investment ? '$' + _fmt(s.investment) : '—' },
          C05_OriginValuation: { textAlign: 'right', color: 'caption', text: (el, s) => s.origin_valuation ? '$' + _fmt(s.origin_valuation) : '—' }
        }
      ),

      AdvisorSection: _section('Advisors', _colsAdvisor, 'advisor', {
        ..._rowBase,
        cursor: 'pointer',
        ':hover': { background: 'overlay' },
        onClick: (e, el, s) => {
          const grant = (s.root?.userGrants || []).find(g => g.id === s._grantId)
          if (grant) s.root.update({ grantDetail: grant })
        },
        C01_Name: { flex: '2', fontWeight: '600', color: 'title', text: (el, s) => s.name },
        C02_Shares: { textAlign: 'right', text: (el, s) => _fmt(s.shares) },
        C03_Equity: { textAlign: 'right', color: 'orange', text: (el, s) => _pct(s.shares) },
        C04_Value: { textAlign: 'right', color: 'caption', text: (el, s) => _valAt(s.shares, s) },
        C05_Start: { color: 'caption', text: (el, s) => s.vesting_start?.slice(0, 10) || '—' },
        C06_End: { color: 'caption', text: (el, s) => s.vesting_end?.slice(0, 10) || '—' },
        C07_Vested: _vestedCell,
        C08_Status: {
          flow: 'x',
          align: 'center left',
          Pill: _StatusPill
        }
      })
    }
  }
}
